import { get, post } from "../helpers/apiHelper";
import { Api } from "../constants";
import {
  Chat,
  ChatMessage,
  ChatMessageApi,
  ChatParticipantDetail,
  ChatParticipantSummary,
  ChatRoomApi,
  ChatRoomHistoryData,
  CreateChatRoomRequest,
  SendChatMessageBody,
  AddMessageReactionBody,
  SendTypingIndicatorBody,
  UserChatRoomsData,
} from "../types/chat";
import { normalizeRoomHistoryPayload } from "../helpers/chatHelper";

export function mapMessageApiToChatMessage(msg: ChatMessageApi): ChatMessage {
  return {
    id: msg.messageId,
    messageId: msg.messageId,
    roomId: msg.roomId,
    senderId: msg.senderId,
    content: msg.content,
    timestamp: msg.timestamp,
    type: msg.type,
    moderated: msg.moderated,
    reactions: msg.reactions || {},
  };
}

/**
 * Normalize Ably (or REST) message payloads: unwrap `{ data: {...} }`, map `id` → `messageId`.
 */
export function normalizeInboundChatMessagePayload(
  raw: unknown
): ChatMessageApi | null {
  let obj: Record<string, unknown> | null = null;
  if (raw && typeof raw === "object") {
    const top = raw as Record<string, unknown>;
    if (
      "data" in top &&
      top.data &&
      typeof top.data === "object" &&
      !Array.isArray(top.data)
    ) {
      obj = top.data as Record<string, unknown>;
    } else {
      obj = top;
    }
  }
  if (!obj) {
    return null;
  }
  const idCandidate = obj.messageId ?? obj.id;
  const messageId =
    idCandidate != null && String(idCandidate).trim() !== ""
      ? String(idCandidate)
      : "";
  if (!messageId) {
    return null;
  }
  const ts = obj.timestamp;
  const timestamp =
    typeof ts === "number"
      ? ts
      : typeof ts === "string"
        ? Number(ts) || 0
        : 0;
  return {
    messageId,
    roomId:
      obj.roomId != null && String(obj.roomId).trim() !== ""
        ? String(obj.roomId)
        : "",
    senderId:
      obj.senderId != null && String(obj.senderId).trim() !== ""
        ? String(obj.senderId)
        : "",
    content: typeof obj.content === "string" ? obj.content : String(obj.content ?? ""),
    timestamp,
    type: typeof obj.type === "string" ? obj.type : "TEXT",
    moderated: Boolean(obj.moderated),
    reactions:
      obj.reactions && typeof obj.reactions === "object" && obj.reactions !== null
        ? (obj.reactions as Record<string, string[]>)
        : null,
  };
}

export function roomApiToChat(room: ChatRoomApi, currentUserId: string): Chat {
  const participants = Array.isArray(room.participants) ? room.participants : [];
  const fallbackOtherUserId =
    participants.find((participant) => participant !== currentUserId) ||
    participants[0] ||
    "";
  const otherUserId = room.otherParticipantDetails?.userId || fallbackOtherUserId;
  const lastMessageTime = room.lastMessage?.timestamp || room.lastActivity || 0;
  return {
    id: room.roomId,
    roomId: room.roomId,
    participants,
    lastActivity: lastMessageTime,
    reactions: room.lastMessage?.reactions || room.reactions || {},
    otherUserId,
    otherUserName: room.otherParticipantDetails?.userName,
    otherUserAvatar:
      room.otherParticipantDetails?.image &&
      typeof room.otherParticipantDetails.image === "string"
        ? room.otherParticipantDetails.image
        : undefined,
    otherUserIsDeactivated: Boolean(room.otherParticipantDetails?.isDeactivated),
    lastMessagePreview: room.lastMessage?.content || "",
    lastMessageTime,
    unreadCount: 0,
  };
}

/** Coalesce concurrent GETs for the same user (e.g. App + selectChatByRoomId). */
let getUserRoomsInFlight: Promise<UserChatRoomsData> | null = null;
let getUserRoomsForId: string | null = null;

function normalizeUserRoomsPayload(data: unknown): ChatRoomApi[] {
  if (!Array.isArray(data)) {
    return [];
  }
  return data as ChatRoomApi[];
}

/**
 * GET /chat/rooms/user/{userId}
 *
 * Returns all chat rooms where `userId` is a participant. Response envelope
 * (`status`, `message`, `data[]`) is unwrapped by `apiHelper.get` to `data`.
 * Auth: Bearer token (axios interceptor, default `userAccessToken`).
 *
 * @see ChatListResponse — full HTTP envelope shape
 */
export const getUserRooms = async (userId: string): Promise<ChatRoomApi[]> => {
  const id = typeof userId === "string" ? userId.trim() : "";
  if (!id) {
    return [];
  }

  if (getUserRoomsInFlight && getUserRoomsForId === id) {
    return getUserRoomsInFlight;
  }

  getUserRoomsForId = id;
  const url = `${Api.chat}/rooms/user/${encodeURIComponent(id)}`;
  getUserRoomsInFlight = get<UserChatRoomsData>(url)
    .then(normalizeUserRoomsPayload)
    .finally(() => {
      getUserRoomsInFlight = null;
      getUserRoomsForId = null;
    });
  return getUserRoomsInFlight;
};

/**
 * POST /chat/rooms — creates a chat room for the given participant user IDs.
 * Body: `{ participants: string[] }`. Uses apiHelper `post` (unwraps envelope `data`, Bearer via axios).
 */
export const createOrGetRoom = async (
  body: CreateChatRoomRequest
): Promise<ChatRoomApi> => {
  const url = `${Api.chat}/rooms`;
  return await post<ChatRoomApi>(url, body);
};

/**
 * GET /chat/rooms/{roomId}/history — full message history (DynamoDB). Bearer via apiHelper.
 */
export const getRoomHistory = async (
  roomId: string
): Promise<{
  messages: ChatMessage[];
  participantDetails: ChatParticipantDetail[];
  otherParticipantDetails: ChatParticipantSummary | null;
}> => {
  const url = `${Api.chat}/rooms/${encodeURIComponent(roomId)}/history`;
  const raw = await get<ChatRoomHistoryData | ChatMessageApi[]>(url);
  const { chatMessages, participantDetails, otherParticipantDetails } =
    normalizeRoomHistoryPayload(raw);
  const messages = chatMessages
    .map(mapMessageApiToChatMessage)
    .sort((a, b) => a.timestamp - b.timestamp);
  return {
    messages,
    participantDetails,
    otherParticipantDetails: otherParticipantDetails ?? null,
  };
};

/**
 * POST /chat/rooms/{roomId}/messages — sends a message; backend publishes to Ably.
 * Body: `{ senderId, content }`. Bearer via axios (apiHelper).
 */
export const sendRoomMessage = async (params: {
  roomId: string;
  senderId: string;
  content: string;
}): Promise<void> => {
  const url = `${Api.chat}/rooms/${encodeURIComponent(params.roomId)}/messages`;
  const body: SendChatMessageBody = {
    senderId: params.senderId,
    content: params.content,
  };
  await post<unknown>(url, body);
};

/**
 * POST /chat/rooms/{roomId}/messages/{messageId}/reactions
 * Body: `{ userId, reaction }`. Publishes to Ably; Bearer via apiHelper.
 */
export const postMessageReaction = async (params: {
  roomId: string;
  messageId: string;
  userId: string;
  reaction: string;
}): Promise<void> => {
  const url = `${Api.chat}/rooms/${encodeURIComponent(params.roomId)}/messages/${encodeURIComponent(params.messageId)}/reactions`;
  const body: AddMessageReactionBody = {
    userId: params.userId,
    reaction: params.reaction,
  };
  await post<unknown>(url, body);
};

/**
 * POST /chat/rooms/{roomId}/typing
 * Body: `{ userId }`. Backend publishes typing to Ably; Bearer via apiHelper.
 */
export const sendTypingIndicator = async (
  roomId: string,
  userId: string
): Promise<void> => {
  const room = typeof roomId === "string" ? roomId.trim() : "";
  const user = typeof userId === "string" ? userId.trim() : "";
  if (!room || !user) {
    return;
  }
  const url = `${Api.chat}/rooms/${encodeURIComponent(room)}/typing`;
  const body: SendTypingIndicatorBody = { userId: user };
  await post<unknown>(url, body);
};
